import { CaretRightIcon, SwapIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { ParameterGroup } from "../generated/api/types.gen";

import {
  applyReplacement,
  type ComponentReplacementSelection,
  canReplaceComponent,
  isGroupReplaced,
  MAX_COMPONENT_REPLACEMENTS,
} from "../lib/component-replacement";
import {
  isParameterResyncEligible,
  validateNotBlanked,
} from "../lib/parameter-panel-helpers";
import { createNamespacedKey } from "../lib/parameter-utils";
import { Button } from "./Button";
import { LoadingSpinner } from "./LoadingSpinner";
import { useParameterForm } from "./ParameterFormContext";
import { ParameterInput } from "./ParameterInput";
import { ReplaceComponentModal } from "./ReplaceComponentModal";

export interface ParameterGroupItemProps {
  group: ParameterGroup;
  groupIndex: number;
  originalParameterGroups?: ParameterGroup[];
  isExpandingThisGroup: boolean;
  defaultCollapsed?: boolean;
}

function AddMoreParametersLink({
  isExpandDisabled,
  isExpandingThisGroup,
  groupName,
  onExpandGroup,
}: {
  isExpandDisabled: boolean;
  isExpandingThisGroup: boolean;
  groupName: string;
  onExpandGroup: (groupName: string) => void;
}) {
  return (
    <div className="flex justify-end pt-1">
      <button
        type="button"
        onClick={() => onExpandGroup(groupName)}
        disabled={isExpandDisabled}
        className="text-[10px] font-medium leading-5 text-[var(--text-secondary)] underline decoration-solid underline-offset-2 transition-colors hover:text-[var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isExpandingThisGroup ? (
          <>
            <LoadingSpinner size="sm" />
            <span>Adding parameters...</span>
          </>
        ) : (
          <span>Add More Parameters</span>
        )}
      </button>
    </div>
  );
}

interface ParameterListProps {
  group: ParameterGroup;
  groupIndex: number;
  originalParameterGroups?: ParameterGroup[];
}

/** Register blank-value rules for every param so collapsed groups (which
 * unmount ParameterInput) still validate on submit.
 *
 * The effect keys on the serialized rules rather than the `group` object.
 * Groups come from a `useWatch`, which hands back fresh objects on every
 * form-state notification — and `unregister` below *is* such a notification,
 * so keying on identity loops: unregister → new group object → effect re-runs
 * → unregister → … Clearing a group's parameters (a component replacement) is
 * what first makes that cycle self-sustaining. */
function useBlankParameterValidation(
  group: ParameterGroup,
  groupIndex: number,
  originalParameterGroups?: ParameterGroup[],
) {
  const { register, unregister } = useFormContext();

  const originalGroup = originalParameterGroups?.find(
    (g) => g.group_name === group.group_name,
  );
  const rulesKey = JSON.stringify(
    group.parameters.map((param, paramIndex) => [
      `parameter_groups.${groupIndex}.parameters.${paramIndex}.value`,
      originalGroup?.parameters.find((p) => p.key === param.key)?.value || "",
    ]),
  );

  useEffect(() => {
    const rules: [string, string][] = JSON.parse(rulesKey);
    for (const [name, originalValue] of rules) {
      register(name, {
        validate: (value: string | null | undefined) =>
          validateNotBlanked(value, originalValue),
      });
    }
    // Drop stale validators when params change/reorder or the group unmounts,
    // keeping the field values so a mounted ParameterInput isn't wiped.
    return () => {
      for (const [name] of rules) {
        unregister(name, { keepValue: true, keepDirty: true });
      }
    };
  }, [rulesKey, register, unregister]);
}

function ParameterList({
  group,
  groupIndex,
  originalParameterGroups,
}: ParameterListProps) {
  const {
    isDirty,
    promptDirty,
    isFormDisabled,
    onParameterChange,
    isResyncingParameters,
    projectId,
  } = useParameterForm();

  return (
    <div className="mt-3 flex gap-3">
      <div
        className="w-px shrink-0 self-stretch bg-[var(--stroke-field)]"
        aria-hidden
      />
      <div className="min-w-0 flex-1 space-y-2">
        {group.parameters.map((param, paramIndex) => {
          const namespacedKey = createNamespacedKey(
            group.group_name,
            param.key,
          );
          const originalGroup = originalParameterGroups?.find(
            (g) => g.group_name === group.group_name,
          );
          const originalParam = originalGroup?.parameters.find(
            (p) => p.key === param.key,
          );
          const originalValue = originalParam?.value || "";
          const isAutoManaged = isParameterResyncEligible(param);
          return (
            <ParameterInput
              key={namespacedKey}
              paramKey={`parameter_groups.${groupIndex}.parameters.${paramIndex}.value`}
              displayName={param.key}
              disabled={isFormDisabled}
              onChange={onParameterChange}
              isModified={
                isDirty && !promptDirty && param.value !== originalValue
              }
              isAutoValue={isAutoManaged}
              groupName={group.group_name}
              projectId={projectId}
              isResyncing={isResyncingParameters && isAutoManaged}
              originalValue={originalValue}
            />
          );
        })}
      </div>
    </div>
  );
}

/** Thumbnail + name shown in place of the group label once replaced. */
function ReplacementChip({ group }: { group: ParameterGroup }) {
  return (
    <span className="flex min-w-0 items-center gap-2 rounded-md bg-white/90 py-1 pl-1 pr-2.5">
      {group.replacement_image_url && (
        <img
          src={group.replacement_image_url}
          alt=""
          className="block size-5 shrink-0 rounded object-cover"
        />
      )}
      <span className="truncate text-xs font-medium text-black">
        {group.replacement_name || group.group_name}
      </span>
    </span>
  );
}

function ReplaceButton({
  onClick,
  disabled,
  groupName,
}: {
  onClick: () => void;
  disabled: boolean;
  groupName: string;
}) {
  return (
    <Button
      variant="toolbar"
      onClick={onClick}
      disabled={disabled}
      aria-label={`Replace ${groupName}`}
      title={
        disabled
          ? `You can replace up to ${MAX_COMPONENT_REPLACEMENTS} components at a time.`
          : undefined
      }
      className="shrink-0"
    >
      Replace
      <SwapIcon size={16} aria-hidden />
    </Button>
  );
}

export function ParameterGroupItem({
  group,
  groupIndex,
  originalParameterGroups,
  isExpandingThisGroup,
  defaultCollapsed = true,
}: ParameterGroupItemProps) {
  const {
    isExpandDisabled,
    onExpandGroup,
    onParameterChange,
    projectId,
    formParameterGroups,
    isFormDisabled,
  } = useParameterForm();
  const { setValue, unregister } = useFormContext();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [isReplaceOpen, setIsReplaceOpen] = useState(false);
  // Keep blank-value rules registered even while the group is collapsed.
  useBlankParameterValidation(group, groupIndex, originalParameterGroups);

  const isReplaced = isGroupReplaced(group);
  if (!(isReplaced || group.parameters?.length)) return null;

  const canReplace =
    !isFormDisabled &&
    canReplaceComponent({
      group,
      formGroups: formParameterGroups,
      originalGroups: originalParameterGroups,
    });

  const handleReplace = (selection: ComponentReplacementSelection) => {
    // Drop the deleted parameters' field entries: their blank-value
    // validators would otherwise still run at submit and block it while
    // "Refine Further" stays enabled. `keepValue` is deliberately omitted —
    // react-hook-form only removes the field when it also drops the value.
    unregister(`parameter_groups.${groupIndex}.parameters`);
    setValue(
      `parameter_groups.${groupIndex}`,
      applyReplacement(group, selection),
      {
        shouldDirty: true,
        shouldTouch: true,
      },
    );
    onParameterChange();
  };

  return (
    <div>
      <div className="flex items-center gap-3 py-1.5">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <button
            type="button"
            className="flex min-w-0 cursor-pointer select-none items-center gap-2 rounded-lg text-left transition-colors"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isReplaced ? (
              <ReplacementChip group={group} />
            ) : (
              <span className="text-heading-category">{group.group_name}</span>
            )}
          </button>
          <ReplaceButton
            onClick={() => setIsReplaceOpen(true)}
            disabled={!canReplace}
            groupName={group.group_name}
          />
        </div>
        <button
          type="button"
          aria-label={isCollapsed ? "Expand group" : "Collapse group"}
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="shrink-0"
        >
          <CaretRightIcon
            size={14}
            weight="bold"
            className={`text-[var(--text-secondary)] transition-transform duration-200 ${!isCollapsed ? "rotate-90" : ""}`}
          />
        </button>
      </div>

      {!(isCollapsed || isReplaced) && (
        <>
          <ParameterList
            group={group}
            groupIndex={groupIndex}
            originalParameterGroups={originalParameterGroups}
          />
          <AddMoreParametersLink
            isExpandDisabled={isExpandDisabled}
            isExpandingThisGroup={isExpandingThisGroup}
            groupName={group.group_name}
            onExpandGroup={onExpandGroup}
          />
        </>
      )}

      <ReplaceComponentModal
        isOpen={isReplaceOpen}
        groupName={group.group_name}
        projectId={projectId}
        onSelect={handleReplace}
        onClose={() => setIsReplaceOpen(false)}
      />
    </div>
  );
}
